'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { Space_Grotesk } from 'next/font/google'
import Logo from '../../_components/Logo'

const spaceGrotesk = Space_Grotesk({ 
  subsets: ['latin'],
  variable: '--font-space-grotesk',
  display: 'swap'
})

const MainNav = () => {
    const [isScrolled, setIsScrolled] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState(null)
    const pathname = usePathname()
    
    // Refs for dropdown containers
    const dropdownRefs = useRef({})

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20)
        }
        
        const handleClickOutside = (event) => {
            // Close dropdown when clicking outside
            if (activeDropdown && !dropdownRefs.current[activeDropdown]?.contains(event.target)) {
                setActiveDropdown(null)
            }
        }
        
        window.addEventListener('scroll', handleScroll)
        document.addEventListener('mousedown', handleClickOutside)
        
        return () => {
            window.removeEventListener('scroll', handleScroll)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [activeDropdown])

    const navigation = [
        
        { name: 'Pricing', href: '/about/pricing' },
        { name: 'Roadmap', href: '/about/roadmap' },
        {
            name: 'About',
            href: '/about',
            dropdownItems: [
                { name: 'Features', href: '/about/features' },
                { name: 'Changelog', href: '/about/changelog' },
                { name: 'Showcase Profiles', href: '/about/showcase' },
                { name: 'Templates', href: '/about/templates' },
            ]
        },
        {
            name: 'Help',
            href: '/support',
            dropdownItems: [
                { name: 'FAQ', href: '/support/faq' },
                { name: 'Contact Us', href: '/support/contact' },
                { name: 'Server Status', href: '/support/status' },
                { name: 'Custom Blocks and Styling', href: '/support/custom-css' },
            ]
        },
        {
            name: 'Legal',
            href: '/legal',
            dropdownItems: [
                { name: 'Privacy Policy', href: '/legal/privacy' },
                { name: 'Terms of Service', href: '/legal/terms' },
                { name: 'Cookie Policy', href: '/legal/cookies' },
                { name: 'Data Processing Agreement (Summit Users)', href: '/legal/dpa' },
            ]
        },
    ]

    const isActive = (path) => {
        return pathname.startsWith(path)
    }

    const handleDropdownToggle = (name) => {
        setActiveDropdown(activeDropdown === name ? null : name)
    }
    return (
        <nav className={`fixed w-full z-50 transition-all duration-300 ${
            isScrolled ? 'bg-neutral-950/80 backdrop-blur-md' : 'bg-transparent'
        }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link href="/" className="flex items-center">
                        <Logo style="name" size="medium" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {navigation.map((item) => (
                            <div
                                key={item.name}
                                className="relative"
                                ref={el => dropdownRefs.current[item.name] = el}
                            >
                                {item.dropdownItems ? (
                                    <>
                                        <div className="flex items-center">
                                            <Link
                                                href={item.href}
                                                className={`text-sm font-medium transition-colors ${
                                                    isActive(item.href)
                                                        ? 'text-indigo-400'
                                                        : 'text-gray-300 hover:text-white'
                                                }`}
                                            >
                                                {item.name}
                                            </Link>
                                            <button
                                                onClick={() => handleDropdownToggle(item.name)}
                                                onMouseEnter={() => setActiveDropdown(item.name)}
                                                className="ml-1 text-gray-300 hover:text-white"
                                            >
                                                <svg 
                                                    xmlns="http://www.w3.org/2000/svg" 
                                                    className={`h-4 w-4 transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`} 
                                                    fill="none" 
                                                    viewBox="0 0 24 24" 
                                                    stroke="currentColor"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        </div>
                                        
                                        <AnimatePresence>
                                            {activeDropdown === item.name && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 10 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-neutral-900 ring-1 ring-black ring-opacity-5 z-50"
                                                    onMouseLeave={() => {
                                                        // Use a small delay to allow for moving to the dropdown
                                                        setTimeout(() => {
                                                            if (!dropdownRefs.current[item.name].contains(document.activeElement)) {
                                                                setActiveDropdown(null)
                                                            }
                                                        }, 100)
                                                    }}
                                                >
                                                    <div className="py-1">
                                                        {item.dropdownItems.map((dropdownItem) => (
                                                            <Link
                                                                key={dropdownItem.name}
                                                                href={dropdownItem.href}
                                                                className={`block px-4 py-2 text-sm ${
                                                                    isActive(dropdownItem.href)
                                                                        ? 'text-indigo-400 bg-neutral-800'
                                                                        : 'text-gray-300 hover:text-white hover:bg-neutral-800'
                                                                }`}
                                                                onClick={() => setActiveDropdown(null)}
                                                            >
                                                                {dropdownItem.name}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                ) : (
                                    <Link
                                        href={item.href}
                                        className={`text-sm font-medium transition-colors ${
                                            isActive(item.href)
                                                ? 'text-indigo-400'
                                                : 'text-gray-300 hover:text-white'
                                        }`}
                                    >
                                        {item.name}
                                    </Link>
                                )}
                            </div>
                        ))}
                        <Link
                            href="/login"
                            className="inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-white hover:bg-neutral-800 transition-colors"
                        >
                            Log In
                        </Link>
                        <Link
                            href="/sign-up"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                        >
                            Get Started
                        </Link>
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-neutral-800 focus:outline-none"
                        >
                            <span className="sr-only">Open main menu</span>
                            {!isMobileMenuOpen ? (
                                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            ) : (
                                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            <motion.div
                initial={false}
                animate={isMobileMenuOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="md:hidden bg-neutral-950/95 backdrop-blur-md overflow-hidden"
            >
                <div className="px-2 pt-2 pb-3 space-y-1">
                    {navigation.map((item) => (
                        <div key={item.name}>
                            <Link
                                href={item.href}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${
                                    isActive(item.href)
                                        ? 'text-indigo-400 bg-neutral-800'
                                        : 'text-gray-300 hover:text-white hover:bg-neutral-800'
                                }`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {item.name}
                            </Link>
                            {item.dropdownItems && (
                                <div className="pl-4">
                                    {item.dropdownItems.map((dropdownItem) => (
                                        <Link
                                            key={dropdownItem.name}
                                            href={dropdownItem.href}
                                            className={`block px-3 py-2 rounded-md text-sm ${
                                                isActive(dropdownItem.href)
                                                    ? 'text-indigo-400 bg-neutral-800'
                                                    : 'text-gray-300 hover:text-white hover:bg-neutral-800'
                                            }`}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {dropdownItem.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    <Link
                        href="/login"
                        className="block px-3 py-2 rounded-md text-base font-medium text-white border border-gray-600 hover:bg-neutral-800"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        Log In
                    </Link>
                    <Link
                        href="/sign-up"
                        className="block px-3 py-2 rounded-md text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        Get Started
                    </Link>
                </div>
            </motion.div>
        </nav>
    )
}

export default MainNav 